import React, { useEffect, useState } from 'react'
import { Box, Button, FormHelperText, Typography } from '@material-ui/core'
import { AddCircleOutline as AddIcon } from '@material-ui/icons'

import useStyles from './styles'

import useFetch from 'hooks/useFetch'
import usePhishing from 'hooks/usePhishing'
import * as service from 'service'

import {
  ExternalSection,
  ParticipantsTable,
  RecipientsDialog,
} from './components'

const RecipientsSelector = ({ error, disabled }) => {
  const classes = useStyles()
  const { phishing, setPhishing } = usePhishing()

  const [open, setOpen] = useState(false)
  const [externalRecipients, setExternalRecipients] = useState(
    () => phishing?.externalRecipients || [],
  )

  useEffect(() => {
    setPhishing((prev) => ({ ...prev, externalRecipients }))
  }, [externalRecipients]) // eslint-disable-line react-hooks/exhaustive-deps

  const { response: responseDepartments, isLoading: isDepartmentsLoading } =
    useFetch(service.dponet.departments.get, {
      perPage: 1000,
      order: 'name ASC',
      minimal: true,
      active: true,
    })

  const handleConfirm = (recipientGroups, uniqueRecipients) => {
    setPhishing((previous) => ({
      ...previous,
      recipients: uniqueRecipients,
      recipientGroups,
    }))
  }

  const handleRemovePlatformUser = (userId, departmentId) => {
    const updatedGroups = (phishing?.recipientGroups || [])
      .map((group) => {
        if (group.id !== departmentId) return group
        return {
          ...group,
          users: group.users.filter((user) => user.id !== userId),
        }
      })
      .filter((group) => group.users.length > 0)

    const uniqueRecipients = [
      ...new Map(
        updatedGroups
          .flatMap((group) => group.users)
          .map((user) => [user.id, user]),
      ).values(),
    ]

    setPhishing((prev) => ({
      ...prev,
      recipientGroups: updatedGroups,
      recipients: uniqueRecipients,
    }))
  }

  const handleRemoveExternal = (email) => {
    setExternalRecipients((prev) => prev.filter((r) => r.email !== email))
  }

  const departments = responseDepartments?.data?.departments || []
  const recipientGroups = phishing?.recipientGroups || []
  const platformRows = recipientGroups.flatMap((group) =>
    group.users.map((user) => ({
      name: user.label,
      email: user.email,
      department: group.name,
      userId: user.id,
      departmentId: group.id,
    })),
  )

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box>
          <Typography variant="h4">Participantes</Typography>
          <Box mt={1} />
          <Typography variant="body2" color="textSecondary">
            Adicione os colaboradores que receberão o e-mail simulado. Você pode
            selecionar usuários já cadastrados na plataforma, adicionar
            participantes externos pelo e-mail ou importar uma lista em massa
            via planilha, sem a necessidade de criar um acesso ao sistema.
          </Typography>
        </Box>
      </Box>

      {error && <FormHelperText error>{error}</FormHelperText>}

      <Box className={classes.sectionBox}>
        <Box className={classes.sectionHeader}>
          <Box>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Colaboradores cadastrados na plataforma
            </Typography>
            <Typography className={classes.sectionSubtitle}>
              Selecione por usuário ou por setor
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpen(true)}
            disabled={disabled}
          >
            Adicionar participantes
          </Button>
        </Box>
      </Box>

      <ExternalSection
        departments={departments}
        isDepartmentsLoading={isDepartmentsLoading}
        disabled={disabled}
        externalRecipients={externalRecipients}
        onAdd={(recipient) =>
          setExternalRecipients((prev) => [...prev, recipient])
        }
      />

      <Box mt={2}>
        <ParticipantsTable
          platformRows={platformRows}
          externalRecipients={externalRecipients}
          disabled={disabled}
          onRemovePlatformUser={handleRemovePlatformUser}
          onRemoveExternal={handleRemoveExternal}
        />
      </Box>

      <RecipientsDialog
        open={open}
        onClose={() => setOpen(false)}
        onConfirm={handleConfirm}
        departments={departments}
        isDepartmentsLoading={isDepartmentsLoading}
        initialRecipientGroups={recipientGroups}
      />
    </Box>
  )
}

export default RecipientsSelector
